import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import Dashboard from './pages/Dashboard';
import Indicators from './pages/Indicators';
import Strategies from './pages/Strategies';
import StrategyDetail from './pages/StrategyDetail';
import Backtest from './pages/Backtest';
import TradeHistory from './pages/TradeHistory';
import Settings from './pages/Settings';
import QuickOrder from './pages/QuickOrder';
import RollingStrangle from './pages/RollingStrangle';
import Positions from './pages/Positions';
import OptionChain from './pages/OptionChain';
import PremiumChart from './pages/PremiumChart';
import DayClassifier from './pages/DayClassifier';
import SignalAlerts from './pages/SignalAlerts';
import WeeklyPlaybook from './pages/WeeklyPlaybook';
import { AppDataProvider, useAppData } from './AppDataContext';
import './App.css';

function OAuthHandler() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const authSuccess = params.get('auth_success');
    const authError = params.get('auth_error');
    if (authSuccess || authError) {
      window.history.replaceState({}, '', location.pathname);
      const msg = authSuccess
        ? `AngelOne account ${authSuccess} connected via OAuth!`
        : `AngelOne login failed: ${authError}`;
      navigate('/settings', { state: { authMessage: msg, authOk: !!authSuccess } });
    }
  }, [location, navigate]);

  return null;
}

function AppShell() {
  const { systemStatus, cycleCount, lastUpdate, indicatorsByTf } = useAppData();

  const getBuySignal = (data) => {
    if (!data) return { state: 'off', text: 'NO BUY' };
    const direct = data.intraday_buy_signal;
    if (direct) return direct === 'BULLISH' ? { state: 'on', text: 'BUY' } : { state: 'off', text: 'NO BUY' };

    const row = (data.indicators || []).find((i) => i.name?.startsWith('Intraday Buy Setup'));
    if (!row) return { state: 'off', text: 'NO BUY' };
    return row.signal === 'BULLISH' ? { state: 'on', text: 'BUY' } : { state: 'off', text: 'NO BUY' };
  };

  const indicatorSignals = {
    tf5m: getBuySignal(indicatorsByTf['5m']),
    tf3m: getBuySignal(indicatorsByTf['3m']),
  };

  return (
    <div className="app">
      <OAuthHandler />
      <Sidebar
        systemStatus={systemStatus}
        cycleCount={cycleCount}
        lastUpdate={lastUpdate}
        indicatorSignals={indicatorSignals}
      />
      <main className="main-content">
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/indicators" element={<Indicators />} />
          <Route path="/strategies" element={<Strategies />} />
          <Route path="/strategies/:name" element={<StrategyDetail />} />
          <Route path="/quick-order" element={<QuickOrder />} />
          <Route path="/manual-trade" element={<QuickOrder />} />
          <Route path="/rolling-strangle" element={<RollingStrangle />} />
          <Route path="/positions" element={<Positions />} />
          <Route path="/premium-chart" element={<PremiumChart />} />
          <Route path="/option-chain" element={<OptionChain />} />
          <Route path="/day-classifier" element={<DayClassifier />} />
          <Route path="/signal-alerts" element={<SignalAlerts />} />
          <Route path="/playbook" element={<WeeklyPlaybook />} />
          <Route path="/backtest" element={<Backtest />} />
          <Route path="/trade-history" element={<TradeHistory />} />
          <Route path="/settings" element={<Settings />} />
        </Routes>
      </main>
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AppDataProvider>
        <AppShell />
      </AppDataProvider>
    </BrowserRouter>
  );
}
